import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, getUnitAnnotation, putUnitAnnotation } from "@/api/client";
import type { UnitAnnotation, UnitAnnotationPut } from "@/api/client";

export type SaveState = "idle" | "saving" | "saved" | "error";

export interface UnitAnnotationState {
  annotation: UnitAnnotation | null;
  error: string | null;
  saveState: SaveState;
  /** Change some fields; the rest are sent as they stand, since a save replaces the whole. */
  save: (change: Partial<UnitAnnotationPut>) => Promise<boolean>;
}

function bodyOf(a: UnitAnnotation): UnitAnnotationPut {
  return {
    notes: a.notes,
    details: a.details,
    shapes: a.shapes,
    reference_instance_id: a.reference_instance_id,
  };
}

/**
 * A type's notes, details and shapes. A save shows at once and is sent in order behind
 * any save still in flight, so two quick changes build on each other instead of the
 * second undoing the first. A failed save puts back what the server last confirmed.
 */
export function useUnitAnnotation(unitId: string | null): UnitAnnotationState {
  const [annotation, setAnnotation] = useState<UnitAnnotation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const latest = useRef<UnitAnnotation | null>(null);
  /** What the server last said, to fall back to when a save fails. */
  const confirmed = useRef<UnitAnnotation | null>(null);
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    let cancelled = false;
    latest.current = null;
    setAnnotation(null);
    setError(null);
    setSaveState("idle");
    if (!unitId) {
      return;
    }
    getUnitAnnotation(unitId)
      .then((got) => {
        if (!cancelled) {
          latest.current = got;
          confirmed.current = got;
          setAnnotation(got);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.detail : "could not load the notes");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  const save = useCallback(
    (change: Partial<UnitAnnotationPut>) => {
      const base = latest.current;
      if (!unitId || !base) {
        return Promise.resolve(false);
      }
      // Shown at once: the next change starts from this one, not from the last answer.
      const body = { ...bodyOf(base), ...change };
      const shown = { ...base, ...body };
      latest.current = shown;
      setAnnotation(shown);
      setSaveState("saving");
      const run = async () => {
        try {
          const saved = await putUnitAnnotation(unitId, body);
          confirmed.current = saved;
          if (latest.current === shown) {
            latest.current = saved;
            setAnnotation(saved);
          }
          setError(null);
          setSaveState("saved");
          return true;
        } catch (err) {
          const back = confirmed.current;
          latest.current = back;
          setAnnotation(back);
          setError(err instanceof ApiError ? err.detail : "could not save");
          setSaveState("error");
          return false;
        }
      };
      const next = queue.current.then(run, run);
      queue.current = next;
      return next;
    },
    [unitId],
  );

  return { annotation, error, saveState, save };
}
